/*
 * NewsletterCTA — reusable subscription call-to-action (Handoff v3 §4).
 * Static-site placeholder: confirms via toast; wire to ESP later.
 */
import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { trpc } from "@/lib/trpc";

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
  }
}

function trackSubscribe(location: string) {
  if (typeof window !== "undefined" && typeof window.gtag === "function") {
    // GA4 recommended "sign_up" event for newsletter subscriptions
    window.gtag("event", "sign_up", {
      method: "newsletter",
      form_location: location,
      page_path: window.location.pathname,
    });
  }
}

export default function NewsletterCTA({
  heading = "The Monthly Edit",
  copy = "One letter, once a month — the stories, the tables, the names to know. Real. Raw. Relevant. Straight to your inbox.",
  location = "inline_cta",
}: {
  heading?: string;
  copy?: string;
  location?: string;
}) {
  const [email, setEmail] = useState("");
  const [notice, setNotice] = useState("");
  const subscribeMutation = trpc.newsletter.subscribe.useMutation({
    onSuccess: () => {
      trackSubscribe(location);
      setNotice("You're on the list. The next Monthly Edit is on its way.");
      setEmail("");
    },
    onError: () => {
      setNotice("We couldn't save your subscription. Please try again.");
    },
  });

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.includes("@")) {
      setNotice("Enter a valid email address.");
      return;
    }
    subscribeMutation.mutate({ email, formLocation: location });
  };

  return (
    <section className="relative overflow-hidden border border-border bg-card">
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.07]"
        style={{
          background:
            "radial-gradient(ellipse at 20% 0%, var(--gold) 0%, transparent 55%), radial-gradient(ellipse at 85% 100%, var(--violet) 0%, transparent 50%)",
        }}
      />
      <div className="relative container py-14 md:py-16">
        <div className="max-w-2xl">
          <p className="kicker">Newsletter</p>
          <h3 className="font-display mt-3 text-3xl md:text-4xl font-bold">
            {heading}
          </h3>
          <p className="mt-3 text-muted-foreground leading-relaxed">{copy}</p>
          <form onSubmit={submit} className="mt-7 flex flex-col sm:flex-row gap-3 max-w-lg">
            <Input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              className="h-12 bg-background/60 border-input text-foreground placeholder:text-muted-foreground/60"
            />
            <Button
              type="submit"
              disabled={subscribeMutation.isPending}
              className="h-12 px-8 border border-[var(--gold)] bg-transparent text-[var(--gold)] font-bold uppercase tracking-[0.18em] text-xs hover:bg-[var(--gold)]/10 hover:text-[var(--gold-2)] active:scale-[0.97] transition-all"
            >
              {subscribeMutation.isPending ? "Joining…" : "Subscribe"}
            </Button>
          </form>
          <p className="mt-3 min-h-5 text-xs text-[var(--violet-2)]" aria-live="polite" role="status">
            {notice}
          </p>
          <p className="mt-3 text-xs text-muted-foreground/70">
            No noise. Unsubscribe anytime.
          </p>
        </div>
      </div>
    </section>
  );
}
